import {
  GoogleMap,
  InfoWindow,
  Marker,
  Polyline,
  useJsApiLoader,
} from "@react-google-maps/api";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { CloseButton } from "../../styledComponents/buttons";
import { PageTitle } from "../../styledComponents/common";

const MapModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
`;

const MapModalContent = styled.div`
  background-color: ${({ theme }) => theme.colors.main.white};
  border-radius: ${({ theme }) => theme.borderRadii.card};
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  width: 90%;
  height: 85%;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const MapHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.secondaryLight};
`;

const MapContainer = styled.div`
  flex-grow: 1;
  width: 100%;
  height: 100%;
  position: relative;
`;

const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

const defaultCenter = {
  lat: 40.7128,
  lng: -74.006,
};

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: true,
  scaleControl: true,
  streetViewControl: true,
  rotateControl: true,
  fullscreenControl: true,
};

const libraries = ["places"];

// Spiderfy radius in degrees (smaller radius for markers closer to center)
const SPIDERFY_RADIUS = 0.0002;

// Helper function to group jobs by coordinates
const groupJobsByCoordinates = jobs => {
  const groups = new Map();

  jobs.forEach((job, index) => {
    const lat = parseFloat(job.latitude);
    const lng = parseFloat(job.longitude);
    const key = `${lat.toFixed(6)}_${lng.toFixed(6)}`;

    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push({ job, index, originalLat: lat, originalLng: lng });
  });

  return groups;
};

// Helper function to calculate spiderfy positions
const calculateSpiderfyPositions = (centerLat, centerLng, count) => {
  const positions = [];
  const angleStep = (2 * Math.PI) / count; // 360 degrees divided by count

  for (let i = 0; i < count; i++) {
    const angle = i * angleStep; // Start from north (0°) and go clockwise
    const offsetLat = SPIDERFY_RADIUS * Math.cos(angle);
    const offsetLng = SPIDERFY_RADIUS * Math.sin(angle);

    positions.push({
      lat: centerLat + offsetLat,
      lng: centerLng + offsetLng,
    });
  }

  return positions;
};

function RouteMapView({ route, onClose }) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_JAVASCRIPT_API_KEY,
    libraries,
  });
  const [map, setMap] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [center, setCenter] = useState(defaultCenter);
  const [pathCoordinates, setPathCoordinates] = useState([]);
  const [spiderfiedJobs, setSpiderfiedJobs] = useState([]);
  const [spiderfyLines, setSpiderfyLines] = useState([]);
  const [centerMarkers, setCenterMarkers] = useState([]);

  useEffect(() => {
    if (route && route.jobs && route.jobs.length > 0) {
      // Get valid job coordinates
      const validJobs = route.jobs.filter(job => job.latitude && job.longitude);

      if (validJobs.length > 0) {
        // Create path from job coordinates (using original coordinates for polyline)
        const path = validJobs.map(job => ({
          lat: parseFloat(job.latitude),
          lng: parseFloat(job.longitude),
        }));

        setPathCoordinates(path);

        // Group jobs by coordinates
        const coordinateGroups = groupJobsByCoordinates(validJobs);

        // Process jobs and apply spiderfy for overlapping markers
        const processedJobs = [];
        const lines = [];
        const centers = [];

        validJobs.forEach((job, index) => {
          const lat = parseFloat(job.latitude);
          const lng = parseFloat(job.longitude);
          const key = `${lat.toFixed(6)}_${lng.toFixed(6)}`;
          const group = coordinateGroups.get(key);

          if (group && group.length > 1) {
            // Multiple jobs at same location - apply spiderfy
            const groupIndex = group.findIndex(item => item.job.id === job.id);
            const spiderfyPositions = calculateSpiderfyPositions(
              lat,
              lng,
              group.length,
            );
            const offsetPosition = spiderfyPositions[groupIndex];

            processedJobs.push({
              ...job,
              displayLat: offsetPosition.lat,
              displayLng: offsetPosition.lng,
              originalLat: lat,
              originalLng: lng,
              originalIndex: index,
              hasSpiderfy: true,
              centerLat: lat,
              centerLng: lng,
            });

            // Add line from center to this marker
            lines.push({
              from: { lat, lng },
              to: { lat: offsetPosition.lat, lng: offsetPosition.lng },
            });

            // Add center marker info (only once per group)
            if (groupIndex === 0) {
              centers.push({
                lat,
                lng,
                jobCount: group.length,
                key,
              });
            }
          } else {
            // Single job at location - no offset needed
            processedJobs.push({
              ...job,
              displayLat: lat,
              displayLng: lng,
              originalLat: lat,
              originalLng: lng,
              originalIndex: index,
              hasSpiderfy: false,
            });
          }
        });

        setSpiderfiedJobs(processedJobs);
        setSpiderfyLines(lines);
        setCenterMarkers(centers);

        // Calculate center point
        const avgLat =
          path.reduce((sum, coord) => sum + coord.lat, 0) / path.length;
        const avgLng =
          path.reduce((sum, coord) => sum + coord.lng, 0) / path.length;

        setCenter({ lat: avgLat, lng: avgLng });

        // Fit bounds to show all markers (including spiderfied ones and centers)
        if (map && processedJobs.length > 0) {
          const bounds = new window.google.maps.LatLngBounds();
          processedJobs.forEach(job => {
            bounds.extend({ lat: job.displayLat, lng: job.displayLng });
          });
          // Include center markers in bounds
          centers.forEach(center => {
            bounds.extend({ lat: center.lat, lng: center.lng });
          });
          map.fitBounds(bounds);
        }
      }
    }
  }, [route, map]);

  const onLoad = React.useCallback(map => {
    setMap(map);
  }, []);

  const onUnmount = React.useCallback(() => {
    setMap(null);
  }, []);

  const getMarkerLabel = index => {
    return {
      text: String(index + 1),
      color: "white",
      fontSize: "12px",
      fontWeight: "bold",
    };
  };

  const getMarkerIcon = (index, total) => {
    // Start marker (green)
    if (index === 0) {
      return {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: "#4CAF50",
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 2,
      };
    }
    // End marker (red)
    else if (index === total - 1) {
      return {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: "#f44336",
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 2,
      };
    }
    // Intermediate markers (blue)
    else {
      return {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: "#2196F3",
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 2,
      };
    }
  };

  const getCenterMarkerIcon = jobCount => {
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: 18, // Bigger center marker
      fillColor: "#FF9800", // Orange color
      fillOpacity: 0.9,
      strokeColor: "#ffffff",
      strokeWeight: 4, // Thicker border
    };
  };

  if (loadError) {
    return (
      <MapModal>
        <MapModalContent>
          <MapHeader>
            <PageTitle>Route Map</PageTitle>
            <CloseButton onClick={onClose}>×</CloseButton>
          </MapHeader>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "#999",
              fontSize: "16px",
            }}
          >
            Error loading Google Maps. Please check your API key.
          </div>
        </MapModalContent>
      </MapModal>
    );
  }

  if (!isLoaded) {
    return (
      <MapModal>
        <MapModalContent>
          <MapHeader>
            <PageTitle>Route Map</PageTitle>
            <CloseButton onClick={onClose}>×</CloseButton>
          </MapHeader>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "#999",
              fontSize: "16px",
            }}
          >
            Loading map...
          </div>
        </MapModalContent>
      </MapModal>
    );
  }

  if (!route || !route.jobs || route.jobs.length === 0) {
    return (
      <MapModal>
        <MapModalContent>
          <MapHeader>
            <PageTitle>Route Map</PageTitle>
            <CloseButton onClick={onClose}>×</CloseButton>
          </MapHeader>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "#999",
              fontSize: "16px",
            }}
          >
            No jobs to display on map
          </div>
        </MapModalContent>
      </MapModal>
    );
  }

  const validJobs = route.jobs.filter(job => job.latitude && job.longitude);

  // Use spiderfied jobs if available, otherwise fall back to validJobs
  const jobsToDisplay =
    spiderfiedJobs.length > 0
      ? spiderfiedJobs
      : validJobs.map((job, index) => ({
          ...job,
          displayLat: parseFloat(job.latitude),
          displayLng: parseFloat(job.longitude),
          originalIndex: index,
        }));

  if (validJobs.length === 0) {
    return (
      <MapModal>
        <MapModalContent>
          <MapHeader>
            <PageTitle>Route for {route.employee_name}</PageTitle>
            <CloseButton onClick={onClose}>×</CloseButton>
          </MapHeader>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "#999",
              fontSize: "16px",
            }}
          >
            No jobs with valid coordinates to display on map
          </div>
        </MapModalContent>
      </MapModal>
    );
  }

  return (
    <MapModal onClick={onClose}>
      <MapModalContent onClick={e => e.stopPropagation()}>
        <MapHeader>
          <PageTitle>Route for {route.employee_name}</PageTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </MapHeader>
        <MapContainer>
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={center}
            zoom={12}
            onLoad={onLoad}
            onUnmount={onUnmount}
            options={mapOptions}
          >
            {/* Route polyline */}
            {pathCoordinates.length > 1 && (
              <Polyline
                path={pathCoordinates}
                options={{
                  strokeColor: "#2266EF",
                  strokeOpacity: 0.8,
                  strokeWeight: 4,
                  geodesic: true,
                }}
              />
            )}

            {/* Spiderfy lines - connect center to each spiderfied marker */}
            {spiderfyLines.map((line, index) => (
              <Polyline
                key={`spiderfy-line-${index}`}
                path={[line.from, line.to]}
                options={{
                  strokeColor: "#FF9800",
                  strokeOpacity: 0.6,
                  strokeWeight: 2,
                  geodesic: false,
                  zIndex: 1,
                }}
              />
            ))}

            {/* Center markers for overlapping job groups */}
            {centerMarkers.map((centerPoint, index) => (
              <Marker
                key={`center-${centerPoint.key}-${index}`}
                position={{
                  lat: centerPoint.lat,
                  lng: centerPoint.lng,
                }}
                icon={getCenterMarkerIcon(centerPoint.jobCount)}
                zIndex={1000} // Ensure center markers appear on top
                title={`${centerPoint.jobCount} jobs at this location`}
              />
            ))}

            {/* Job markers */}
            {jobsToDisplay.map((job, index) => (
              <Marker
                key={job.id}
                position={{
                  lat: job.displayLat,
                  lng: job.displayLng,
                }}
                label={getMarkerLabel(
                  job.originalIndex !== undefined ? job.originalIndex : index,
                )}
                icon={getMarkerIcon(
                  job.originalIndex !== undefined ? job.originalIndex : index,
                  validJobs.length,
                )}
                onClick={() => setSelectedMarker(job)}
              />
            ))}

            {/* Info window for selected marker */}
            {selectedMarker && (
              <InfoWindow
                position={{
                  lat:
                    selectedMarker.displayLat ||
                    parseFloat(selectedMarker.latitude),
                  lng:
                    selectedMarker.displayLng ||
                    parseFloat(selectedMarker.longitude),
                }}
                onCloseClick={() => setSelectedMarker(null)}
              >
                <div style={{ padding: "5px" }}>
                  <h4
                    style={{
                      margin: "0 0 8px 0",
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                  >
                    {selectedMarker.name}
                  </h4>
                  {selectedMarker.address && (
                    <p
                      style={{
                        margin: "4px 0",
                        fontSize: "12px",
                        color: "#666",
                      }}
                    >
                      📍 {selectedMarker.address}
                    </p>
                  )}
                  {selectedMarker.status && (
                    <p
                      style={{
                        margin: "4px 0",
                        fontSize: "12px",
                        color: "#666",
                      }}
                    >
                      Status: {selectedMarker.status}
                    </p>
                  )}
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </MapContainer>
      </MapModalContent>
    </MapModal>
  );
}

export default RouteMapView;
